import { useMemo } from 'react'
import { eventRegionId } from '@shared/regions'
import { useVenues } from './useVenues'
import type { Event } from '../types'

/**
 * Region id per event: its stamped `regionId`, else derived by joining its
 * venue to the static city table in `@shared/regions`. Events with no venue
 * fall back to their free-text `location`; anything still unresolved is simply
 * absent from the map and is treated as "not near you".
 *
 * `loading` is only true while venues an unstamped event needs are still
 * arriving with nothing cached. Callers render their normal list until it flips.
 */
export function useEventRegions(events: Event[]): {
  regions: Map<string, string>
  loading: boolean
} {
  // Hand the venue ids these events need to useVenues, so a venue seeded since
  // the cache was written is fetched now rather than after the TTL. A stamped
  // event needs none.
  const venueIds = useMemo(
    () => [...new Set(events.filter(e => !e.regionId && e.venueId).map(e => e.venueId!))],
    [events],
  )
  const { venues, loading } = useVenues(venueIds)

  const regions = useMemo(() => {
    const map = new Map<string, string>()
    for (const event of events) {
      const venue = event.venueId ? venues.get(event.venueId) : undefined
      const regionId = eventRegionId(event, venue)
      if (regionId) map.set(event.id, regionId)
    }
    return map
  }, [events, venues])

  return { regions, loading: loading && venueIds.length > 0 }
}
